import React, {useState} from 'react';
import {Stack, FormControlLabel, RadioGroup, TextField} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Body,
  designSystemToken,
} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import type {CancellationReason} from '@/api/updaters/contracts';
import CheckmarkRadioButton from '@/components/checkmark-radio-button';
import FlashMessage from '@/components/flash-message';
import useCancelContract from '@/hooks/mutations/use-cancel-contract';

const MESSAGE_MAX_LENGTH = 500;

const CANCELLATION_REASONS = [
  {value: 1, label: 'Unresponsive'},
  {value: 2, label: 'Missed deadlines'},
  {value: 3, label: 'Not available'},
  {value: 4, label: 'Other'},
] as const;

type CancelContractModalProps = {
  open: boolean;
  onClose: () => void;
  contractId: string;
  creatorName?: string;
  onSuccess?: () => void;
};

function CancelContractModal({
  open,
  onClose,
  contractId,
  creatorName,
  onSuccess,
}: CancelContractModalProps) {
  const cancelContract = useCancelContract();

  const [reason, setReason] = useState<number | null>(null);
  const [message, setMessage] = useState('');

  const canSubmit = reason !== null && message.trim().length > 0;

  const handleClose = () => {
    if (cancelContract.isLoading) return;
    setReason(null);
    setMessage('');
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit || reason === null) return;
    AnalyticsService.dispatchEvent('cancel-contract', {
      event_type: 'cancel contract',
      category: 'Campaign Participants UI',
      label: {cancellation_reason: reason, cancellation_message: message},
    });

    cancelContract.mutate(
      {
        contractId,
        cancellationReason: reason as CancellationReason,
        cancellationMessage: message,
      },
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Creator removed')}
                subtitle={creatorName ?? ''}
              />
            ),
          });
          setReason(null);
          setMessage('');
          onSuccess?.();
          onClose();
        },
        onError: (error: unknown) => {
          raiseFlashMessageError({
            title: translate('Failed to remove creator'),
            subtitle: extractBackendErrorDetail(error),
          });
        },
      }
    );
  };

  return (
    <Modal open={open} handleClose={handleClose} size="small" showCloseButton>
      <Stack gap="16px" padding="24px" alignItems="flex-start">
        <Headline size="md">{translate('Remove creator')}</Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            'Choose a reason and write a short message. The creator will be notified.'
          )}
        </Body>

        <RadioGroup
          value={reason ?? ''}
          onChange={(_, v) => setReason(Number(v))}
        >
          {CANCELLATION_REASONS.map((r) => (
            <FormControlLabel
              key={r.value}
              value={r.value}
              control={<CheckmarkRadioButton />}
              label={translate(r.label)}
            />
          ))}
        </RadioGroup>

        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder={translate('Message to creator')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          inputProps={{maxLength: MESSAGE_MAX_LENGTH}}
          helperText={`${message.length}/${MESSAGE_MAX_LENGTH}`}
        />

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap="12px"
          sx={{width: '100%'}}
        >
          <Button
            mode="tinted"
            appearance="neutral"
            size="medium"
            onClick={handleClose}
            disabled={cancelContract.isLoading}
          >
            {translate('Cancel')}
          </Button>
          <Button
            mode="filled"
            appearance="danger"
            size="medium"
            onClick={handleSubmit}
            disabled={!canSubmit || cancelContract.isLoading}
          >
            {translate('Remove')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default CancelContractModal;
